// GuidesListScreen — lists all available destination guides for SEO.
// Mounted at /guides — fetches from GET /api/guides and renders a grid of cards.
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { B } from '../constants';
import { guides } from '../api';

export default function GuidesListScreen() {
  const navigate = useNavigate();
  const [guidesList, setGuidesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    guides
      .list()
      .then((data) => {
        if (!cancelled) setGuidesList(data.guides || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load guides');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    (guidesList || []).forEach((g) => {
      const tag = g.tag || 'Other';
      if (!map[tag]) map[tag] = [];
      map[tag].push(g);
    });
    return map;
  }, [guidesList]);

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.sandPattern }}>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Loading guides…</p>
      </div>
    );
  }

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', padding: '40px 24px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 48, maxWidth: 720, margin: '0 auto 48px' }}>
        <p style={{ fontFamily: "'Montserrat',sans-serif", letterSpacing: 2, fontSize: 12, fontWeight: 700, color: B.forest, marginBottom: 12, textTransform: 'uppercase' }}>
          GREATBREAK GUIDES
        </p>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 48, fontWeight: 700, color: B.night, marginBottom: 16 }}>
          Travel Guides
        </h1>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, color: B.muted, lineHeight: 1.7 }}>
          Expert-curated destination guides with day-by-day itineraries, top experiences, and insider tips.
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, color: '#dc3545', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Guide cards grid */}
      {guidesList.length === 0 ? (
        <p style={{ textAlign: 'center', color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
          No guides available yet. Check back soon!
        </p>
      ) : (
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {Object.entries(grouped).map(([tag, items]) => (
            <div key={tag} style={{ marginBottom: 40 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, color: B.night, marginBottom: 20, paddingBottom: 8, borderBottom: `2px solid ${B.forest}` }}>
                {tag}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                {items.map((guide) => (
                  <div
                    key={guide.id}
                    onClick={() => navigate(`/guide/${guide.slug}`)}
                    style={{
                      background: 'white',
                      borderRadius: 16,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                  >
                    <div style={{ height: 180, background: `url(${guide.img}) center/cover` }} />
                    <div style={{ padding: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: B.forest }}>
                          {guide.tag || 'Destination'}
                        </span>
                        {guide.rating && (
                          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.amber, fontWeight: 600 }}>
                            ⭐ {guide.rating}
                          </span>
                        )}
                      </div>
                      <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 600, color: B.night, marginBottom: 8 }}>
                        {guide.name}
                        {guide.hasRichContent && (
                          <span
                            title="Detailed guide available"
                            style={{
                              marginLeft: 8, fontSize: 10, padding: '2px 6px',
                              background: B.forest, color: '#fff', borderRadius: 4,
                              fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
                              letterSpacing: 1, verticalAlign: 'middle',
                            }}
                          >
                            DETAILED
                          </span>
                        )}
                      </h3>
                      <div style={{ display: 'flex', gap: 16, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted }}>
                        <span>{guide.days} days</span>
                        <span>${guide.budget}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
